import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  ArrowLeft,
  Building2,
  MapPin,
  Users,
  DollarSign,
  FileText,
  Image as ImageIcon,
  CheckCircle,
  XCircle,
  Loader2,
  Calendar,
  User,
} from 'lucide-react';
import { branchRequestService } from '../services';
import { BranchRequest } from '../types';

export const BranchRequestDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [request, setRequest] = useState<BranchRequest | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject'>('approve');

  useEffect(() => {
    if (id) {
      loadRequest(parseInt(id));
    }
  }, [id]);

  const loadRequest = async (requestId: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await branchRequestService.getRequestById(requestId);
      setRequest(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load branch request');
    } finally {
      setLoading(false);
    }
  };

  const handleReviewClick = (action: 'approve' | 'reject') => {
    setReviewAction(action);
    setReviewNotes('');
    setReviewDialogOpen(true);
  };

  const handleReviewSubmit = async () => {
    if (!request) return;

    try {
      setError(null);
      await branchRequestService.reviewBranchRequest(request.id, {
        status: reviewAction === 'approve' ? 'APPROVED' : 'REJECTED',
        reviewNotes: reviewNotes || undefined,
      });
      setReviewDialogOpen(false);
      setReviewNotes('');
      await loadRequest(request.id);
    } catch (err: any) {
      setError(err.message || 'Failed to review branch request');
    }
  };

  const getStatusVariant = (status: string): 'default' | 'secondary' | 'destructive' => {
    switch (status) {
      case 'APPROVED':
        return 'default';
      case 'REJECTED':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const getRequestTypeLabel = (type: string) => {
    switch (type) {
      case 'CREATE':
        return 'Create New Branch';
      case 'UPDATE':
        return 'Update Existing Branch';
      default:
        return type;
    }
  };

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/branch-requests')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Branch Requests
        </Button>
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <p className="font-medium">Error</p>
          <p className="text-sm">{error || 'Branch request not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/branch-requests')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{request.name}</h1>
            <p className="text-muted-foreground">{getRequestTypeLabel(request.requestType)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={getStatusVariant(request.status)}>{request.status}</Badge>
          {request.status === 'PENDING' && (
            <>
              <Button variant="outline" onClick={() => handleReviewClick('reject')}>
                <XCircle className="h-4 w-4 mr-2" />
                Reject
              </Button>
              <Button onClick={() => handleReviewClick('approve')}>
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve
              </Button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <p className="font-medium">Error</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Request Information */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <Building2 className="h-4 w-4" />
                Organization
              </div>
              <p className="text-base">{request.organization?.name || '-'}</p>
            </div>

            <Separator />

            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <MapPin className="h-4 w-4" />
                Location
              </div>
              <p className="text-base">{request.address}</p>
              <p className="text-sm text-muted-foreground">{request.city?.name || '-'}</p>
            </div>

            {request.description && (
              <>
                <Separator />
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-1">Description</div>
                  <p className="text-base">{request.description}</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Business Details */}
        <Card>
          <CardHeader>
            <CardTitle>Business Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-1">
                Business Categories
              </div>
              <div className="flex flex-wrap gap-2">
                {request.businessCategories?.map((category) => (
                  <Badge key={category.id} variant="outline">
                    {category.emoji} {category.name}
                  </Badge>
                ))}
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                  <DollarSign className="h-4 w-4" />
                  Commission Rate
                </div>
                <p className="text-base">
                  {request.commissionRate ? `${request.commissionRate}%` : '-'}
                </p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                  <DollarSign className="h-4 w-4" />
                  Base Commission
                </div>
                <p className="text-base">
                  {request.baseCommission ? `$${request.baseCommission}` : '-'}
                </p>
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                  <Users className="h-4 w-4" />
                  Min People
                </div>
                <p className="text-base">{request.minPeople || '-'}</p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                  <Users className="h-4 w-4" />
                  Max People
                </div>
                <p className="text-base">{request.maxPeople || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Documents and Images */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Documents */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Documents ({request.documents?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {request.documents && request.documents.length > 0 ? (
              <div className="space-y-2">
                {request.documents.map((doc, index) => (
                  <a
                    key={index}
                    href={doc}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2 rounded hover:bg-accent text-sm"
                  >
                    <FileText className="h-4 w-4" />
                    Document {index + 1}
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No documents attached</p>
            )}
          </CardContent>
        </Card>

        {/* Images */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5" />
              Images ({request.images?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {request.images && request.images.length > 0 ? (
              <div className="grid grid-cols-3 gap-2">
                {request.images.map((img, index) => (
                  <a
                    key={index}
                    href={img}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="aspect-square rounded-lg overflow-hidden border hover:opacity-80 transition-opacity"
                  >
                    <img
                      src={img}
                      alt={`Branch image ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No images attached</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Request Metadata */}
      <Card>
        <CardHeader>
          <CardTitle>Request Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <User className="h-4 w-4" />
                Requested By
              </div>
              <p className="text-base">{request.requestedBy?.fullName || '-'}</p>
              <p className="text-sm text-muted-foreground">{request.requestedBy?.email || ''}</p>
            </div>

            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <Calendar className="h-4 w-4" />
                Request Date
              </div>
              <p className="text-base">{new Date(request.createdAt).toLocaleString()}</p>
            </div>
          </div>

          {request.reviewedBy && (
            <>
              <Separator />
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                    <User className="h-4 w-4" />
                    Reviewed By
                  </div>
                  <p className="text-base">{request.reviewedBy.fullName || '-'}</p>
                  <p className="text-sm text-muted-foreground">{request.reviewedBy.email || ''}</p>
                </div>

                <div>
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                    <Calendar className="h-4 w-4" />
                    Review Date
                  </div>
                  <p className="text-base">
                    {request.reviewedAt ? new Date(request.reviewedAt).toLocaleString() : '-'}
                  </p>
                </div>
              </div>
            </>
          )}

          {request.reviewNotes && (
            <>
              <Separator />
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-1">Review Notes</div>
                <p className="text-base">{request.reviewNotes}</p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reviewAction === 'approve' ? 'Approve' : 'Reject'} Branch Request
            </DialogTitle>
            <DialogDescription>
              {reviewAction === 'approve'
                ? `Approve the branch request for "${request.name}"?`
                : `Reject the branch request for "${request.name}"?`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="reviewNotes">Notes (Optional)</Label>
              <Textarea
                id="reviewNotes"
                value={reviewNotes}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setReviewNotes(e.target.value)
                }
                placeholder="Add any notes about this decision..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant={reviewAction === 'approve' ? 'default' : 'destructive'}
              onClick={handleReviewSubmit}
            >
              {reviewAction === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BranchRequestDetailPage;
